import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { AxiosError } from 'axios';
import { CUSTOMER_FORM_URL } from '../../../../config';

import {
    Box,
    Button,
    FormControl,
    FormHelperText,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';

import OverlayLoader from '../../../../components/overlayLoader';
import EnquiryService from '../../../../services/api/enquiry';
import referralCodeService from '../../../../services/api/referralCode';
import { useAuth } from '../../../../services/auth/AuthContext';


const getEnquiryInputsProps = (enquiryOptions) => [
    {
        name: 'referral_code_id',
        label: 'Select Referral Code',
        options: enquiryOptions.assigned_referrals.map(({ id, referral_code, referrer }) => ({
            value: id,
            label: referral_code + (referrer ? ` (${referrer.first_name} ${referrer.last_name})` : ''),
        })),
    },
    {
        name: 'preferred_language',
        label: 'Select Language',
        options: enquiryOptions.languages.map(({ code, name }) => ({
            value: code,
            label: name,
        })),
    },
    {
        name: 'loan_type_id',
        label: 'Select Loan Type',
        options: enquiryOptions.loan_types.map(({ id, name }) => ({
            value: id,
            label: name,
        })),
    },
    {
        name: 'state_id',
        label: 'Select State',
        options: enquiryOptions.service_states.map(({ id, name }) => ({
            value: id,
            label: name,
        })),
    },
    {
        name: 'income_type_id',
        label: 'Customer Type',
        options: enquiryOptions.income_types.map(({ id, name }) => ({
            value: id,
            label: name,
        })),
    },
    {
        name: 'gender',
        label: 'Gender',
        options: (enquiryOptions.field_options?.enums?.gender ?? []).map(({ value, label }) => ({
            value,
            label,
        })),
    },
    {
        name: 'loan_purpose',
        label: 'Loan Purpose',
        options: (enquiryOptions.field_options?.enums?.loan_purpose ?? []).map(({ value, label }) => ({
            value,
            label,
        })),
    },
];

const basicInfoInputsProps = [
    {
        name: 'first_name',
        label: 'Applicant First Name',
        helperText: 'Enter as per Aadhaar ID',
        type: 'text',
        required: true,
    },
    {
        name: 'last_name',
        label: 'Applicant Last Name',
        helperText: 'Enter as per Aadhaar ID',
        type: 'text',
        required: true,
    },
    {
        name: 'pan_number',
        label: 'PAN Number',
        helperText: 'Five letters, four digits, then a letter',
        type: 'text',
        required: true,
    },
    {
        name: 'date_of_birth',
        label: 'Date of Birth',
        type: 'date',
        required: true,
    },
    {
        name: 'current_address',
        label: 'Current Address',
        helperText: 'Where the applicant lives, not the property address',
        type: 'text',
        required: true,
    },
    {
        name: 'business_pincode',
        label: 'Business Pincode',
        type: 'number',
        required: true,
    },
    {
        name: 'lead_sourcing_date',
        label: 'Lead Sourcing Date',
        helperText: 'When this lead was sourced',
        type: 'date',
        required: true,
    },
];

const loanRequirementsInputsProps = [
    {
        name: 'required_loan_amount',
        label: 'Required Loan Amount',
        helperText: '(in Indian Rupees)',
        type: 'number',
        required: true,
    },
    {
        name: 'requested_tenure_months',
        label: 'Requested Tenure',
        helperText: 'In months, not years',
        type: 'number',
        required: true,
    },
    {
        name: 'expected_roi',
        label: 'Expected / Quoted ROI',
        helperText: 'Optional. Annual percentage',
        type: 'number',
        required: false,
    },
];

// The bounds, formats and option lists all come from the API. They used to be
// written out here, which is how this form ended up rejecting loan amounts the
// partner API accepted: the ceiling moved to 10 crore in common/questions/
// limits.py and these copies kept the old 99,99,999. One definition, read at
// runtime, cannot drift.
const bound = (fieldOptions, name) => {
    const entry = fieldOptions?.bounds?.[name];
    return {
        min: entry ? Number(entry.min) : undefined,
        max: entry ? Number(entry.max) : undefined,
    };
};

const withBounds = (schema, { min, max }) => {
    let bounded = schema;
    if (min !== undefined) bounded = bounded.min(min);
    if (max !== undefined) bounded = bounded.max(max);
    return bounded;
};

const buildCreateEnquirySchema = (fieldOptions) => {
    const panPattern = fieldOptions?.formats?.pan_number;
    const pincodeDigits = fieldOptions?.formats?.pincode_digits ?? 6;
    const age = bound(fieldOptions, 'applicant_age_years');

    return z.object({
        whatsapp_number: z.string().regex(/^\d{10}$/, 'Whatsapp number should have 10 digits'),
        referral_code_id: z.string().min(1, 'Please enter at least 5 digits').optional(),
        preferred_language: z.string(),
        loan_type_id: z.coerce.number(),
        state_id: z.coerce.number(),
        income_type_id: z.coerce.number(),
        gender: z.string().nonempty('Please select a gender'),
        loan_purpose: z.string().nonempty('Please select a loan purpose'),

        first_name: z.string().trim().nonempty(),
        last_name: z.string().trim(),
        // Uppercased before matching: the backend normalises the same way, so a
        // lowercase PAN is accepted rather than rejected on a formatting nicety.
        pan_number: z
            .string()
            .trim()
            .transform((value) => value.toUpperCase())
            .refine(
                (value) => (panPattern ? new RegExp(panPattern).test(value) : true),
                'PAN should be five letters, four digits, then a letter',
            ),
        date_of_birth: z
            .string()
            .nonempty('Please enter a date of birth')
            .refine((value) => {
                if (age.min === undefined || age.max === undefined) return true;
                const dob = new Date(value);
                const today = new Date();
                let years = today.getFullYear() - dob.getFullYear();
                const beforeBirthday =
                    today.getMonth() < dob.getMonth() ||
                    (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate());
                if (beforeBirthday) years -= 1;
                return years >= age.min && years <= age.max;
            }, `Applicant age must be between ${age.min ?? '?'} and ${age.max ?? '?'}`),
        current_address: z.string().trim().nonempty('Please enter the current address'),
        business_pincode: z
            .string()
            .regex(new RegExp(`^\\d{${pincodeDigits}}$`), `Pincode should have ${pincodeDigits} digits`),
        lead_sourcing_date: z
            .string()
            .nonempty('Please enter the lead sourcing date')
            .refine((value) => {
                const sourced = new Date(value);
                const today = new Date();
                today.setHours(23, 59, 59, 999);
                if (sourced > today) return false;
                const maxAge = fieldOptions?.rules?.lead_sourcing_date_max_age_days;
                if (!maxAge) return true;
                const ageDays = (Date.now() - sourced.getTime()) / 86400000;
                return ageDays <= maxAge;
            }, 'Lead sourcing date cannot be in the future or too old'),

        required_loan_amount: withBounds(z.coerce.number(), bound(fieldOptions, 'required_loan_amount')),
        requested_tenure_months: withBounds(
            z.coerce.number(),
            bound(fieldOptions, 'requested_tenure_months'),
        ),
        // Optional: an empty input must mean "not supplied", not zero.
        expected_roi: z
            .union([z.literal(''), withBounds(z.coerce.number(), bound(fieldOptions, 'expected_roi'))])
            .optional(),
    });
};


export default function CreateEnquiryForm() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const hasCreatedSuccessfully = searchParams.get('success');
    const enquiryId = searchParams.get('enquiry_id');
    const flowToken = searchParams.get('flow_token')

    const [enquiryOptions, setEnquiryOptions] = useState(null);
    const createEnquirySchema = useMemo(
        () => buildCreateEnquirySchema(enquiryOptions?.field_options),
        [enquiryOptions],
    );
    const { setIsAuthenticated, user } = useAuth();
    const userRoles = user.role;
    const isSupport = userRoles.includes('support');
    const isPartner = userRoles.includes('partner');
    const isBCPartner = userRoles.includes('bc_partner');
    const showingContinueApplicationButton = (isSupport || isPartner) && !isBCPartner;

    const [enquirySubmitErrors, setEnquirySubmitErrors] = useState({});
    const [formErrors, setFormErrors] = useState({});

    const onInputFieldChange = (e) => {
        const { name, value } = e.target;
        if (formErrors[name] === undefined) return;

        const { success, error } = createEnquirySchema
            .shape[name]
            .safeParse(value);

        setFormErrors((prev) => ({
            ...prev,
            [name]: !success ? error.issues[0].message : '',
        }));
    };

    const handleEnquirySubmit = async (e) => {
        e.preventDefault();
        setFormErrors({});
        setEnquirySubmitErrors({});

        const formData = Object.fromEntries(new FormData(e.target));
        const result = createEnquirySchema.safeParse(formData);
        if (!result.success) {
            setFormErrors(result.error.formErrors.fieldErrors);
            return;
        }

        const payload = result.data;
        payload.whatsapp_number = '91' + payload.whatsapp_number;
        if (payload.expected_roi === '' || payload.expected_roi === undefined) {
            delete payload.expected_roi;
        }
        try {
            const response = await EnquiryService.createEnquiry(payload, setIsAuthenticated, navigate);
            setSearchParams((searchParams) => {
                searchParams.set('success', true);
                searchParams.set('enquiry_id', response.enquiry_id);
                searchParams.set('flow_token', response.enquiry_flow_token);
                return searchParams;
            });
        } catch (err) {
            if (err instanceof AxiosError) {
                const res = err.response;
                if (res) {
                    if (res.status === 400) {
                        setEnquirySubmitErrors(res.data);
                    }
                }
            }
        }
    };

    useEffect(() => {
        EnquiryService.getEnquiryOptions(setIsAuthenticated, navigate)
            .then(setEnquiryOptions);
    }, [navigate, setIsAuthenticated]);

    if (hasCreatedSuccessfully) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    mt: 2,
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: "calc(60vh - 64px)",
                    flexDirection: 'column',
                    maxWidth: 400,
                    mx: 'auto',
                }}
            >
                <Typography variant="h5" component={'h2'} textAlign="center">
                    Enquiry Created Successfully!
                </Typography>
                <Button
                    fullWidth
                    onClick={() => navigate(`/search?search_by=enquiry_id&search_term=${enquiryId}`)}
                    variant="contained"
                    color="primary"
                >
                    View Enquiry
                </Button>
                {showingContinueApplicationButton && (
                    <Button
                        fullWidth
                        onClick={() => window.open(`${CUSTOMER_FORM_URL}/verification/${flowToken}`, '_blank')}
                        variant="contained"
                        color="primary"
                    >
                        Continue Application
                    </Button>
                )}
            </Box>
        );
    }

    if (!enquiryOptions) {
        return (
            <OverlayLoader
                open
                text="Loading enquiry form..."
            />
        );
    }

    const enquiryInputsProps = getEnquiryInputsProps(enquiryOptions);

    return (
        <>
            <Typography variant="h4" component={'h1'} textAlign="center">
                Create New Enquiry
            </Typography>

            <Box
                component={'form'}
                onSubmit={handleEnquirySubmit}
                display={'flex'}
                flexDirection={'column'}
                gap={2}
                maxWidth={400}
                mx={'auto'}
                mt={3}
            >
                <TextField
                    name="whatsapp_number"
                    label="Phone Number (WhatsApp preferred)"
                    type="number"
                    size="small"
                    required
                    error={!!formErrors.whatsapp_number || !!enquirySubmitErrors.whatsapp_number}
                    helperText={formErrors.whatsapp_number || enquirySubmitErrors.whatsapp_number}
                    onChange={onInputFieldChange}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    +91
                                </InputAdornment>
                            ),
                        },
                    }}
                />

                {enquiryInputsProps.map((input) => (
                    <FormControl
                        key={input.name}
                        size="small"
                        required
                        error={!!formErrors[input.name] || !!enquirySubmitErrors[input.name]}
                    >
                        <InputLabel id={input.name}>
                            {input.label}
                        </InputLabel>

                        <Select
                            name={input.name}
                            labelId={input.name}
                            label={input.label}
                            defaultValue={''}
                            onChange={onInputFieldChange}
                        >
                            {input.options.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>

                        <FormHelperText>
                            {formErrors[input.name] || enquirySubmitErrors[input.name]}
                        </FormHelperText>
                    </FormControl>
                ))}

                <Box mt={2}>
                    <Typography
                        variant="h5"
                        component={'h2'}
                        mb={2}
                    >
                        Basic Information
                    </Typography>

                    <Box
                        display={'flex'}
                        flexDirection={'column'}
                        gap={2}
                    >
                        {basicInfoInputsProps.map(({ helperText, ...inputProps }) => (
                            <TextField
                                key={inputProps.name}
                                size="small"
                                error={!!formErrors[inputProps.name] || !!enquirySubmitErrors[inputProps.name]}
                                helperText={formErrors[inputProps.name] || enquirySubmitErrors[inputProps.name] || helperText}
                                onChange={onInputFieldChange}
                                defaultValue={
                                    inputProps.name === 'lead_sourcing_date'
                                        ? new Date().toISOString().slice(0, 10)
                                        : undefined
                                }
                                slotProps={
                                    inputProps.type === 'date'
                                        ? { inputLabel: { shrink: true } }
                                        : undefined
                                }
                                {...inputProps}
                            />
                        ))}
                    </Box>
                </Box>

                <Box mt={2}>
                    <Typography
                        variant="h5"
                        component={'h2'}
                        mb={2}
                    >
                        Loan Requirements
                    </Typography>

                    <Box
                        display={'flex'}
                        flexDirection={'column'}
                        gap={2}
                    >
                        {loanRequirementsInputsProps.map(({ helperText, ...inputProps }) => (
                            <TextField
                                key={inputProps.name}
                                size="small"
                                error={!!formErrors[inputProps.name] || !!enquirySubmitErrors[inputProps.name]}
                                helperText={formErrors[inputProps.name] || enquirySubmitErrors[inputProps.name] || helperText}
                                onChange={onInputFieldChange}
                                {...inputProps}
                            />
                        ))}
                    </Box>
                </Box>

                {(
                    Object.values(formErrors).some((value) => !!value) ||
                    Object.values(enquirySubmitErrors).some((value) => !!value)
                ) && (
                    <Typography color="error" fontSize={'small'}>
                        There are some errors in the form. Please correct them.
                    </Typography>
                )}

                <Button
                    type="submit"
                    size="small"
                    variant="contained"
                >
                    Create
                </Button>
            </Box>
        </>
    );
}
